// Creator Documents — docs shared with me.
// Route: /creator/documents
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { apiClient, useAuth } from "../auth/AuthContext";
import { BrandFooter } from "../components/brand/Footer";
import { LogoLockup } from "../components/brand/LogoLockup";

function CreatorNav({ onLogout }) {
  return (
    <header className="border-b border-brand-text/10 bg-brand-bg/80 backdrop-blur">
      <div className="mx-auto flex max-w-5xl items-center justify-between gap-4 px-6 py-4">
        <div className="flex items-center gap-6">
          <LogoLockup variant="header" as={Link} to="/creator" />
          <nav className="hidden gap-6 text-[10px] uppercase tracking-[0.28em] text-brand-text/50 md:flex">
            <Link data-testid="nav-dashboard" to="/creator" className="hover:text-brand-gold">Dashboard</Link>
            <Link data-testid="nav-learning" to="/creator/learning" className="hover:text-brand-gold">Learning</Link>
            <Link data-testid="nav-documents" to="/creator/documents" className="text-brand-gold">Documents</Link>
            <Link data-testid="nav-profile" to="/profile" className="hover:text-brand-gold">My Profile</Link>
            <Link data-testid="nav-contract" to="/contract" className="hover:text-brand-gold">My Contract</Link>
          </nav>
        </div>
        <button data-testid="nav-signout" onClick={onLogout} className="rounded-full border border-brand-text/15 px-4 py-2 text-[10px] uppercase tracking-[0.28em] text-brand-text/60 hover:border-brand-gold/40 hover:text-brand-gold">Sign out</button>
      </div>
    </header>
  );
}

const humanBytes = (n) => {
  if (!n && n !== 0) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
};

export default function CreatorDocuments() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  useEffect(() => { document.title = "Documents · Omertàbella"; }, []);
  useEffect(() => {
    apiClient.get("/me/documents").then((r) => setItems(r.data.items)).finally(() => setLoading(false));
  }, []);
  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return items;
    return items.filter((d) =>
      (d.display_label || "").toLowerCase().includes(needle) ||
      (d.category_label || "").toLowerCase().includes(needle));
  }, [items, q]);
  const onSignOut = async () => { await logout(); navigate("/", { replace: true }); };
  const BACKEND = process.env.REACT_APP_BACKEND_URL;

  return (
    <main data-testid="creator-documents" className="min-h-screen bg-brand-bg text-brand-text">
      <CreatorNav onLogout={onSignOut} />
      <div className="mx-auto max-w-5xl px-6 py-12 sm:py-16">
        <p className="font-mono text-[10px] uppercase tracking-[0.32em] text-brand-gold">Your files</p>
        <h1 className="mt-3 font-display text-4xl text-brand-text sm:text-5xl lg:text-6xl">Documents from your team<span className="text-brand-gold">.</span></h1>
        <p className="mt-3 max-w-2xl text-sm text-brand-text/60">Playbooks, contracts, and briefs sent to you by leadership. Download links expire after 30 days.</p>

        <input data-testid="doc-search" type="search" value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Search…"
          className="mt-8 w-full max-w-md rounded-full border border-brand-text/15 bg-[#141414] px-4 py-2 text-sm text-brand-text focus:border-brand-gold focus:outline-none" />

        <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {loading && <div className="col-span-full py-16 text-center font-mono text-[10px] uppercase tracking-[0.32em] text-brand-text/45">Loading…</div>}
          {!loading && filtered.length === 0 && (
            <div data-testid="documents-empty" className="col-span-full rounded-2xl border border-brand-text/10 bg-[#141414] p-12 text-center text-sm text-brand-text/50">
              Nothing shared with you yet. Leadership will send documents here as they come up.
            </div>
          )}
          {filtered.map((d) => (
            <div key={d.share_id} data-testid={`doc-card-${d.share_id}`} className="flex flex-col overflow-hidden rounded-2xl border border-brand-text/10 bg-[#141414]">
              <div className="flex h-24 items-center justify-center bg-gradient-to-br from-brand-gold/20 via-brand-bg to-brand-bg">
                <span className="font-display text-4xl text-brand-gold/60">
                  {(d.content_type || "").includes("pdf") ? "PDF" : (d.content_type || "").includes("image") ? "IMG" : "DOC"}
                </span>
              </div>
              <div className="flex flex-1 flex-col gap-2 p-4">
                <span className="inline-block w-fit rounded-full border border-brand-gold/40 bg-brand-gold/10 px-2 py-0.5 font-mono text-[9px] uppercase tracking-[0.24em] text-brand-gold">{d.category_label}</span>
                <h3 className="font-display text-lg text-brand-text">{d.display_label}</h3>
                <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-brand-text/45">{humanBytes(d.size_bytes)} · sent by {d.sent_by_name}</p>
                <p className="font-mono text-[10px] text-brand-text/45">{d.sent_at ? new Date(d.sent_at).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : ""}</p>
                <div className="mt-auto flex items-center justify-between pt-3">
                  <a data-testid={`doc-download-${d.share_id}`} href={`${BACKEND}/api/documents/download/${d.download_token}`} target="_blank" rel="noreferrer"
                    className="rounded-full border border-brand-gold bg-brand-gold px-4 py-1.5 text-[10px] uppercase tracking-[0.24em] text-brand-bg hover:bg-brand-bg hover:text-brand-gold">
                    Download
                  </a>
                  {d.downloaded_at && <span data-testid={`doc-downloaded-${d.share_id}`} className="font-mono text-[10px] uppercase tracking-[0.24em] text-emerald-300">✓ Opened</span>}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      <BrandFooter />
    </main>
  );
}
